/**
 * 사용량 지표 — **나중에 다시 셀 수 없는 사실만 적재한다.**
 *
 * 계획서(§5 "SaaS 표면")가 이것 하나를 P2 로 앞당긴 이유가 시점이다: 과금 설계는
 * 컷오버 뒤지만 **히스토리는 그때 만들 수 없다.** 오늘 안 적은 오늘 치는 영원히 없다.
 * 그래서 읽는 화면도, API 도 아직 없는 채로 적재만 먼저 선다 — 이 표의 독자는
 * "가격을 정하는 날의 우리"이고 그 날은 아직 안 왔다.
 *
 * ─── `irreproducible` 이 필수 필드인 이유 ───
 * 이 표의 유일한 실패 모드는 **재구성 가능한 것을 적재하는 것**이다. 근태 건수·휴가
 * 신청 수·급여 확정 건수는 전부 각 표의 `created_at` 으로 나중에도 셀 수 있고, 그걸
 * 매일 또 적으면 같은 사실이 두 곳에 살면서 언젠가 갈라진다 —
 * `vacation_balances.remaining_days` 가 정확히 그 자리였다(0014: 33행 중 15행이
 * 원장으로 설명되지 않았다). 그래서 지표를 하나 늘릴 때 **왜 못 세는지**를 적어야 하고,
 * 그 문장을 못 쓰면 그것은 지표가 아니라 쿼리다.
 *
 * ─── 오늘 셋뿐인 이유 (전부 "못 센다"가 참이다) ───
 *  · `core.seats.active`          재직 판정의 입력(`joined_on`·`left_on`)은 **정정된다.**
 *                                 청구의 근거가 뒤에서 움직이면 그건 청구가 아니다.
 *                                 (0021 이 `half_days` 를 저장으로 뒤집은 것과 같은 논거 —
 *                                  "저장하지 않는다"의 예외는 값이 나중에 움직일 때다)
 *  · `core.seats.unknownJoinDate` 위의 짝. 입사일은 나중에 채워지고, 채워지면 "그달에는
 *                                 몰랐다"가 안 남는다. **셋째가 늦게 붙은 이유는 §브라우저**
 *                                 — 마이그레이션 0024 의 주석은 그래서 아직 "둘"이라고
 *                                 적혀 있다(적용된 마이그레이션은 고치지 않는다, 규칙 4).
 *  · `core.modules.enabled`       `tenant_modules` 에 **이력이 없다.** 껐다 켜면 그 사이가
 *                                 흔적 없이 사라지고, 모듈-일 정산은 그 사이를 못 청구한다.
 *
 * ─── §브라우저 ───
 * 셋째는 게이트가 아니라 **화면을 열어서** 나왔다(2026-09-05). 자동 작업 화면의 결과가
 * `재직 0명` 이었는데 개발 테넌트에는 직원이 10명 있었다 — 전부 입사일이 없어서다.
 * 게이트 12종은 그때도 전부 초록이었다(AGENTS.md 23 의 그 자리).
 */

/**
 * 지표의 종류. **오늘은 `gauge` 하나다.**
 *
 * `counter`(그날 몇 건 일어났나)를 일부러 안 만들었다 — 오늘 세고 싶은 카운터가 전부
 * 재구성 가능하기 때문이다. 필요한 날 이 유니온을 늘리고, 그때 쓰기 경로도 갈라진다
 * (gauge 는 그날 한 번 관측이고 counter 는 누적이라 `on conflict` 의 동작이 다르다).
 */
export type UsageMetricKind = 'gauge';

/** `subject` 컬럼에 무엇이 오는가. 값 자체가 아니라 **축의 종류**다. */
export type UsageMetricSubject = 'none' | 'module';

export interface UsageMetricDef {
  /** `<moduleKey>.<rest>`. 1번 자리가 소유자다 — 잡 키·i18n 키와 같은 규약, 같은 이유. */
  readonly key: string;
  readonly kind: UsageMetricKind;
  /**
   * 세부 축. `none` 이면 그 지표의 행은 하루에 하나이고 `subject` 는 빈 문자열이다.
   * nullable 로 두지 않은 이유: UNIQUE 에 NULL 이 들어가면 같은 (날, 지표)가 여러 번
   * 들어갈 수 있고, 그건 멱등성이 인덱스라는 주장을 조용히 무르는 것이다.
   */
  readonly subject: UsageMetricSubject;
  /** **왜 나중에 다시 셀 수 없는가.** 이 문장을 못 쓰면 적재하지 않는다. */
  readonly irreproducible: string;
}

export class UsageMetricDeclarationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UsageMetricDeclarationError';
  }
}

/** 잡 키와 같은 모양이다. 1번 자리가 소유 모듈이고, 그래야 해지한 모듈의 지표를 들어낼 수 있다. */
const USAGE_METRIC_KEY_RE = /^[a-z][a-z0-9]*(\.[a-zA-Z][a-zA-Z0-9]*)+$/;

/**
 * 지표를 만드는 **유일한 방법.** 잘못된 선언은 배포가 아니라 import 시점에 죽는다
 * (`defineJob`·`procedure` 와 같은 수법).
 */
export function defineUsageMetric(def: UsageMetricDef): UsageMetricDef {
  if (!USAGE_METRIC_KEY_RE.test(def.key)) {
    throw new UsageMetricDeclarationError(
      `사용량 지표 키 '${def.key}' 가 <moduleKey>.<rest> 형태가 아니다. ` +
        `1번 자리는 소유 모듈이고, 모듈을 해지할 때 접두사 하나로 그 지표를 들어낸다.`,
    );
  }
  if (!def.irreproducible.trim()) {
    throw new UsageMetricDeclarationError(
      `사용량 지표 '${def.key}' 에 irreproducible 이 비어 있다. ` +
        `"왜 나중에 다시 셀 수 없는가"를 못 쓰면 그것은 지표가 아니라 쿼리이고, ` +
        `적재하는 순간 같은 사실이 두 곳에 살게 된다.`,
    );
  }
  return def;
}

/** 그날 재직 중이던 직원 수. 판정은 `employeeInService`(packages/core) 하나가 한다. */
export const USAGE_SEATS_ACTIVE = defineUsageMetric({
  key: 'core.seats.active',
  kind: 'gauge',
  subject: 'none',
  irreproducible:
    '재직 판정의 입력(joined_on·left_on)은 관리자가 정정한다. 지난달 청구의 근거가 ' +
    '이번 달에 움직이면 그건 청구가 아니다. 구 시스템에서 입사일 없는 직원이 4명 있었고 ' +
    '퇴사일은 나중에 채워졌다(0018 실측).',
});

/** 그날 켜져 있던 모듈. `subject` 가 모듈 키이고 값은 1 — 합이 곧 **모듈-일** 이다. */
export const USAGE_MODULES_ENABLED = defineUsageMetric({
  key: 'core.modules.enabled',
  kind: 'gauge',
  subject: 'module',
  irreproducible:
    'core.tenant_modules 에 이력이 없다. enabled_at/disabled_at 한 쌍뿐이라 껐다 켜면 ' +
    '그 사이가 흔적 없이 사라지고, 모듈-일 정산은 사라진 구간을 청구할 수도 환불할 수도 없다.',
});

/**
 * 재직 판정을 **못 한** 직원 수(입사일이 없다). `core.seats.active` 의 짝이다.
 *
 * ★ 2026-09-05 에 브라우저에서 보고 붙였다. 개발 테넌트의 재직자가 `0명` 으로 떴는데
 * 직원은 10명 있었다 — 전부 입사일이 없어서다(같은 화면의 월차 잡이 `입사일 없음 10명`
 * 이라고 이미 말하고 있었다). 이 지표가 없으면 **청구액 0 과 "직원이 없는 회사"가 같은
 * 모양**이고, 그 차이는 돈이다. 0018 이 구 잡의 `skipped_count` 하나로는 아무것도 못
 * 고친다고 적은 자리와 같은 판단이다 — 세는 것을 갈라 놓는다.
 *
 * 0 으로 접거나 재직으로 추정하지 않는다. 모르는 것을 `true` 로 읽는 것이 구 시스템이
 * 퇴사자에게 월차를 준 경로다(`employeeInService` 의 주석).
 */
export const USAGE_SEATS_UNKNOWN = defineUsageMetric({
  key: 'core.seats.unknownJoinDate',
  kind: 'gauge',
  subject: 'none',
  irreproducible:
    '입사일은 나중에 채워진다 — 채워지고 나면 "그달에는 몰랐다"는 사실이 어디에도 안 남고, ' +
    '지난달 청구가 왜 낮았는지 설명할 수 없게 된다. seats.active 와 같은 이유의 같은 축이다.',
});

/** 선언된 지표 전부. **목록이 하나다** — 적재하는 것도 읽는 것도 이 배열이다. */
export const USAGE_METRICS: readonly UsageMetricDef[] = [
  USAGE_SEATS_ACTIVE,
  USAGE_SEATS_UNKNOWN,
  USAGE_MODULES_ENABLED,
];

/** 적재 한 건. 날짜는 잡이 정하므로 여기 없다 — 한 번의 관측은 하루에 속한다. */
export interface UsageObservation {
  readonly metric: string;
  /** `subject: 'none'` 인 지표는 빈 문자열이다. NULL 이 아닌 이유는 UNIQUE 다. */
  readonly subject: string;
  readonly value: number;
}
